import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { FaMapMarkerAlt, FaHotel, FaChevronLeft, FaChevronRight, FaCamera } from "react-icons/fa";
import { LanguageContext } from '../LanguageContext';

// Import des icônes de services
import hotelIcon from "../img/hotel.png";
import transportIcon from "../img/avion.png";
import visaIcon from "../img/visa.png";
import guideIcon from "../img/Expert.png";

// Import des images principales
import heroBgImage from "../img/mecca.jpg";

// Import des images d'hôtels - Médine
import darAlIman1 from "../img/darAlIman1.jpg";
import darAlIman2 from "../img/darAlIman2.jpg";
import darAlIman3 from "../img/darAlIman3.jpg";
import darAlIman4 from "../img/darAlIman4.jpg";
import taibaFront2 from "../img/taibaFront2.jpg";
import taibaFront3 from "../img/taibaFront3.jpg";
import taibaFront4 from "../img/taibaFront4.jpg";
import zamzamPullman1 from "../img/zamzamPullman1.jpg";
import zamzamPullman2 from "../img/zamzamPullman2.jpg";
import zamzamPullman3 from "../img/zamzamPullman3.jpg";
import zamzamPullman4 from "../img/zamzamPullman4.jpg";

// Import des images d'hôtels - Mecque
import swissotel1 from "../img/swissotel1.jpg";
import swissotel2 from "../img/swissotel2.jpg";
import hiltonJabal1 from "../img/hiltonJabal1.jpg";
import hiltonJabal2 from "../img/hiltonJabal2.jpg";
import alMarwaRayhaan1 from "../img/alMarwaRayhaan1.jpg";
import alMarwaRayhaan2 from "../img/alMarwaRayhaan2.jpg";
import alMarwaRayhaan3 from "../img/alMarwaRayhaan3.jpg";
import alMarwaRayhaan4 from "../img/alMarwaRayhaan4.jpg";




// Import des images des lieux - Médine
import rawdaSharifa from "../img/rawdaSharifa.jpeg";
import rawdaSharifa2 from "../img/rawdaSharifa2.png";
import rawdaSharifa3 from "../img/rawdaSharifa3.jpg";
import masjidQuba from "../img/masjidQuba1.jpg";
import masjidQuba2 from "../img/masjidQuba2.jpg";
import masjidQuba3 from "../img/masjidQuba3.jpg";
import masjidUhud from "../img/masjidUhud5.jpg";
import masjidUhud3 from "../img/masjidUhud2.jpg";
import masjidUhud4 from "../img/masjidUhud3.jpg";
import masjidUhud5 from "../img/masjidUhud4.jpg";
import baqiCemetery from "../img/baqiCemetery.jpg";
import baqiCemetery2 from "../img/baqiCemetery2.jpg";
import palmOasis from "../img/palmOasis.jpg";

// Import des images des lieux - Mecque
import jabalArafat from "../img/jabalArafat.jpg";
import caveHira from "../img/caveHira.jpg";
import caveThawr from "../img/caveThawr.jpg";
import mina from "../img/mina.jpg";
import muzdalifa from "../img/muzdalifa.jpg";
import masjidNimra from "../img/masjidNimra.jpg";

// Composant Galerie 
const Gallery = ({ items, title, language }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showGallery, setShowGallery] = useState(false);

    const nextSlide = useCallback(() => {
        setCurrentIndex(prev => (prev + 1) % items.length);
    }, [items.length]);

    const prevSlide = useCallback(() => {
        setCurrentIndex(prev => (prev - 1 + items.length) % items.length);
    }, [items.length]);

    return (
        <div className="mb-8">
            <h3 className="text-xl font-semibold mb-3 flex items-center">
                <FaCamera className="mr-2 text-green-600" />
                {title}
                <button 
                    onClick={() => setShowGallery(!showGallery)}
                    className="ml-2 text-sm bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded transition-colors"
                >
                    {showGallery ? (language === 'fr' ? 'Masquer' : 'إخفاء') : (language === 'fr' ? 'Voir la galerie' : 'عرض المعرض')}
                </button>
            </h3>
            
            {showGallery && (
                <div className="relative h-64 md:h-96 overflow-hidden rounded-lg shadow-lg bg-black mb-4">
                    <img 
                        src={items[currentIndex]} 
                        alt={title}
                        className="w-full h-full object-cover opacity-90"
                    />
                    
                    <div className="absolute inset-0 flex items-center justify-between p-4">
                        <button 
                            onClick={prevSlide}
                            className="bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full p-2 transition-all shadow-md"
                            aria-label={language === 'fr' ? 'Précédent' : 'السابق'}
                        >
                            <FaChevronLeft className="text-gray-800 text-xl" />
                        </button>
                        <button 
                            onClick={nextSlide}
                            className="bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full p-2 transition-all shadow-md"
                            aria-label={language === 'fr' ? 'Suivant' : 'التالي'}
                        >
                            <FaChevronRight className="text-gray-800 text-xl" />
                        </button>
                    </div>
                    
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                        {items.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentIndex(index)}
                                className={`w-3 h-3 rounded-full transition-all ${currentIndex === index ? 'bg-white' : 'bg-white bg-opacity-50'}`}
                                aria-label={`${index + 1} ${language === 'fr' ? 'sur' : 'من'} ${items.length}`}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Composant pour un service inclus
const ServiceItem = ({ image, title, description }) => (
    <div className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
        <img src={image} alt={title} width={80} height={80} className="mb-4" />
        <h3 className="text-lg font-semibold text-green-600 mb-2">{title}</h3>
        <p className="text-gray-700 text-sm">{description}</p>
    </div>
);

const OmraAoutPage = () => {
    const [activeTab, setActiveTab] = useState("Medine");
    const { language } = useContext(LanguageContext);
    const googleFormUrl = "https://forms.gle/yGF9HpcLfn2JRRa99";

    // Galeries des hôtels
   const hotelGalleries = {
    Medine: [
        {
            name: language === 'fr' ? "Dar Al Iman Intercontinental" : "دار الإيمان إنتركونتيننتال",
            images: [darAlIman1, darAlIman2, darAlIman3, darAlIman4],
            distance: language === 'fr' ? "À 200m de la Masjid an-Nabawi" : "على بعد 200 متر من المسجد النبوي",
            category: "★★★★★",
            features: [
                language === 'fr' ? "Chambres luxueuses avec vue sur la mosquée" : "غرف فاخرة بإطلالة على المسجد",
                language === 'fr' ? "Restaurants internationaux" : "مطاعم عالمية",
                language === 'fr' ? "Spa et centre de bien-être" : "سبا ومركز لياقة"
            ]
        },
        {
            name: language === 'fr' ? "Taiba Front" : "طيفة فرونت",
            images: [taibaFront2, taibaFront3, taibaFront4],
            distance: language === 'fr' ? "À 150m de la Masjid an-Nabawi" : "على بعد 150 متر من المسجد النبawi",
            category: "★★★★",
            features: [
                language === 'fr' ? "Accès direct à la mosquée" : "وصول مباشر للمسجد",
                language === 'fr' ? "Service de chambre 24h/24" : "خدمة الغرف على مدار الساعة",
                language === 'fr' ? "Wi-Fi haut débit" : "واي فاي عالي السرعة"
            ]
        },
        {
            name: language === 'fr' ? "Zamzam Pullman" : "زمزم بولمان",
            images: [zamzamPullman1, zamzamPullman2, zamzamPullman3, zamzamPullman4],
            distance: language === 'fr' ? "À 300m de la Masjid an-Nabawi" : "على بعد 300 متر من المسجد النبوي",
            category: "★★★★★",
            features: [
                language === 'fr' ? "Piscine intérieure" : "مسبح داخلي",
                language === 'fr' ? "Salles de conférence" : "قاعات مؤتمرات",
                language === 'fr' ? "Service de navette gratuit" : "خدمة نقل مجانية"
            ]
        }
    ],
    Mecque: [
        {
            name: language === 'fr' ? "Swissotel Al Maqam" : "سويسوتيل المقام",
            images: [swissotel1, swissotel2],
            distance: language === 'fr' ? "À 100m de la Masjid al-Haram" : "على بعد 100 متر من المسجد الحرام",
            category: "★★★★★",
            features: [
                language === 'fr' ? "Vue directe sur la Kaaba" : "إطلالة مباشرة على الكعبة",
                language === 'fr' ? "Restaurants gastronomiques" : "مطاعم فاخرة",
                language === 'fr' ? "Centre d'affaires" : "مركز أعمال"
            ]
        },
        {
            name: language === 'fr' ? "Hilton Jabal Ali" : "هيلتون جبل علي",
            images: [hiltonJabal1, hiltonJabal2],
            distance: language === 'fr' ? "À 400m de la Masjid al-Haram" : "على بعد 400 متر من المسجد الحرام",
            category: "★★★★★",
            features: [
                language === 'fr' ? "Toit-terrasse avec vue panoramique" : "سطح مع إطلالة بانورامية",
                language === 'fr' ? "Salle de sport moderne" : "صالة ألعاب رياضية حديثة",
                language === 'fr' ? "Service de conciergerie" : "خدمة الكونسيرج"
            ]
        },
        // Nouvelle entrée pour Al Marwa Rayhaan by Rotana - Makkah
        {
            name: language === 'fr' ? "Al Marwa Rayhaan by Rotana - Makkah" : "المروا ريحان من روتانا - مكة",
            images: [alMarwaRayhaan1, alMarwaRayhaan2, alMarwaRayhaan3, alMarwaRayhaan4], 
            distance: language === 'fr' ? "À 50m de la Masjid al-Haram" : "على بعد 50 متر من المسجد الحرام",
            category: "★★★★★",
            features: [
                language === 'fr' ? "Emplacement privilégié face à la Kaaba" : "موقع متميز أمام الكعبة",
                language === 'fr' ? "Centre commercial intégré" : "مركز تسوق متكامل",
                language === 'fr' ? "Chambres et suites spacieuses" : "غرف وأجنحة واسعة",
                language === 'fr' ? "Plusieurs options de restauration" : "خيارات متعددة لتناول الطعام"
            ]
        }
    ]
};

// Galeries des lieux à visiter
const placesGalleries = {
    Medine: [
        {
            name: language === 'fr' ? "La Rawda Ash-Sharifa" : "الروضة الشريفة",
            images: [rawdaSharifa, rawdaSharifa2, rawdaSharifa3],
            description: language === 'fr' ? "Espace sacré entre le minbar et la tombe du Prophète (ﷺ)" : "المكان المقدس بين المنبر وقبر النبي (ﷺ)"
        },
        {
            name: language === 'fr' ? "Mosquée de Quba" : "مسجد قباء",
            images: [masjidQuba, masjidQuba2, masjidQuba3],
            description: language === 'fr' ? "Première mosquée de l'Islam construite par le Prophète (ﷺ)" : "أول مسجد في الإسلام بناه النبي (ﷺ)"
        },
        {
            name: language === 'fr' ? "Mont Uhud" : "جبل أحد",
            images: [masjidUhud, masjidUhud3, masjidUhud4, masjidUhud5],
            description: language === 'fr' ? "Site de la célèbre bataille où reposent les martyrs" : "موقع المعركة الشهيرة حيث يرقد الشهداء"
        },
        {
            name: language === 'fr' ? "Cimetière Al-Baqi" : "مقبرة البقيع",
            images: [baqiCemetery, baqiCemetery2],
            description: language === 'fr' ? "Lieu de sépulture de nombreux compagnons du Prophète (ﷺ)" : "مكان دفن العديد من أصحاب النبي (ﷺ)"
        },
        {
            name: language === 'fr' ? "Oasis de palmiers" : "واحة النخيل",
            images: [palmOasis],
            description: language === 'fr' ? "Site historique avec des palmiers datant de l'époque du Prophète (ﷺ)" : "موقع تاريخي به نخيل يعود إلى عهد النبي (ﷺ)"
        }
    ],
    Mecque: [
        {
            name: language === 'fr' ? "Mont Arafat" : "جبل عرفة",
            images: [jabalArafat],
            description: language === 'fr' ? "Lieu central du Hajj où le Prophète (ﷺ) a prononcé son dernier sermon" : "الموقع المركزي للحج حيث ألقى النبي (ﷺ) خطبته الأخيرة"
        },
        {
            name: language === 'fr' ? "Grotte de Hira" : "غار حراء",
            images: [caveHira],
            description: language === 'fr' ? "Lieu de la première révélation du Coran" : "مكان نزول أول وحي قرآني"
        },
        {
            name: language === 'fr' ? "Grotte de Thawr" : "غار ثور",
            images: [caveThawr],
            description: language === 'fr' ? "Lieu où le Prophète (ﷺ) s'est réfugié pendant la Hijra" : "مكان اختبأ فيه النبي (ﷺ) أثناء الهجرة"
        },
        {
            name: language === 'fr' ? "Mina" : "منى",
            images: [mina],
            description: language === 'fr' ? "Site des stèles de la lapidation pendant le Hajj" : "موقع رمي الجمرات أثناء الحج"
        },
        {
            name: language === 'fr' ? "Muzdalifa" : "مزدلفة",
            images: [muzdalifa],
            description: language === 'fr' ? "Lieu de rassemblement entre Arafat et Mina pendant le Hajj" : "مكان المبيت بين عرفة ومنى أثناء الحج"
        },
        {
            name: language === 'fr' ? "Mosquée de Nimra" : "مسجد نمرة",
            images: [masjidNimra],
            description: language === 'fr' ? "Mosquée où les pèlerins prient le jour d'Arafat" : "المسجد الذي يصلي فيه الحجاج يوم عرفة"
        }
    ]
};
    const tabs = {
        fr: ["Medine", "Mecque", "Infos & Tarifs"],
        ar: ["المدينة", "مكة", "المعلومات والأسعار"]
    };

    const tabKeys = {
        fr: { "Medine": "Medine", "Mecque": "Mecque", "Infos & Tarifs": "Infos & Tarifs" },
        ar: { "المدينة": "Medine", "مكة": "Mecque", "المعلومات والأسعار": "Infos & Tarifs" }
    };

    const content = {
        fr: {
            title: "Omra",
            subtitle: "Un voyage spirituel inoubliable avec un service haut de gamme",
            servicesTitle: "Nos Services Inclus",
            programmeTitle: "Programme Détaillé de l'Omra",
            medineTitle: "Votre Séjour à Médine",
            mekkaTitle: "Votre Omra à la Mecque",
            infosTitle: "Informations Pratiques & Tarifs",
            reserveButton: "Réserver maintenant",
            discoverButton: "Découvrir le Programme",
            medineContent: {
                title: "Omra - Médine",
                description: "Médine, anciennement Yathrib, est la deuxième ville sainte de l'Islam où le Prophète (ﷺ) a établi la première communauté musulmane après l'Hégire. Ville d'accueil du Messager d'Allah, elle abrite la Mosquée du Prophète (Al-Masjid an-Nabawi) et de nombreux sites historiques.",
                duration: "7 nuits à Médine"
            },
            mekkaContent: {
                title: "Omra - La Mecque",
                description: "La Mecque (Makkah), berceau de l'Islam où naquit le Prophète Muhammad (ﷺ), abrite la Kaaba vers laquelle se dirigent toutes les prières musulmanes. Ville sanctuaire et destination du pèlerinage depuis plus de 1400 ans.",
                duration: "7 nuits à La Mecque"
            },
            infosContent: {
                includes: [
                    "Vol direct avec Saudi Airlines (2 valises de 23 kg incluses)",
                    "Visites guidées à Médine et à La Mecque avec guide francophone",
                    "Accompagnement religieux",
                    "Assistance complète pour l'obtention du visa",
                    "Hébergement dans des hôtels de luxe proches des lieux saints",
                    "Ziyarats organisées avec transports"
                ],
                excludes: [
                    "Frais personnels et dépenses individuelles",
                    "Les taxis et transports non prévus au programme",
                    
                   
                ],
                documents: [
                    "Une photo couleur récente sur fond blanc (format passeport)",
                    "Passeport valable 6 mois après le retour",
                    "Carte de résident (pour les passeports étrangers)",
                   // "Acompte de 50% pour valider l'inscription"
                ],
                prices: [
                    "À partir de 2950 CHF par personne",
                   // "Supplément single: +30%",
                    //"Enfant (2-11 ans): -20%"
                ],
                payment: {
                    methods: ["Virement bancaire", "Chèque", "Espèces"],
                   schedule: [
                       "50% à la réservation",
                        "50% avant le départ"
                    ]
                }
            }
        },
        ar: {
            title: "عمرة 2025",
            subtitle: "رحلة روحية لا تنسى مع خدمة فاخرة",
            servicesTitle: "خدماتنا المدرجة",
            programmeTitle: "البرنامج التفصيلي للعمرة",
            medineTitle: "إقامتك في المدينة المنورة",
            mekkaTitle: "عمرتك في مكة المكرمة",
            infosTitle: "معلومات عملية وأسعار",
            reserveButton: "احجز الآن",
            discoverButton: "اكتشف البرنامج",
            medineContent: {
                title: "عمرة - المدينة المنورة",
                description: "المدينة المنورة، المعروفة سابقًا باسم يثرب، هي المدينة الثانية المقدسة في الإسلام حيث أسس النبي (ﷺ) أول مجتمع مسلم بعد الهجرة. مدينة استقبال رسول الله، تضم المسجد النبوي والعديد من المواقع التاريخية.",
                duration: "7 ليال في المدينة"
            },
            mekkaContent: {
                title: "عمرة - مكة المكرمة",
                description: "مكة المكرمة، مهد الإسلام حيث ولد النبي محمد (ﷺ)، تضم الكعبة التي تتجه إليها جميع الصلوات الإسلامية. مدينة مقدسة ووجهة الحج منذ أكثر من 1400 عام.",
                duration: "7 ليال في مكة"
            },
            infosContent: {
                includes: [
                    "رحلة طيران مباشرة مع الخطوط السعودية (2 حقيبة 23 كجم مشمولة)",
                    "زيارات موجهة في المدينة ومكة مع مرشد ناطق بالفرنسية",
                    "مرافقة دينية",
                    "مساعدة كاملة للحصول على التأشيرة",
                    "الإقامة في فنادق فاخرة قريبة من الأماكن المقدسة",
                    "زيارات منظمة مع مواصلات"
                ],
                excludes: [
                    "المصاريف الشخصية والنفقات الفردية",
                    "سيارات الأجرة والمواصلات غير المدرجة في البرنامج",
                    
                ],
                documents: [
                    "صورة ملونة حديثة على خلفية بيضاء (مقاس جواز السفر)",
                    "جواز سفر ساري المفعول لمدة 6 أشهر بعد العودة",
                    "بطاقة الإقامة (لحاملي الجوازات الأجنبية)",
                    "دفعة مقدمة 50% لتأكيد الحجز"
                ],
                prices: [
                    "ابتداءً من 2950 يورو للشخص",
                    "غرفة فردية: +30%",
                    "الأطفال (2-11 سنة): -20%"
                ],
                payment: {
                    methods: ["حوالة بنكية", "شيك", "نقدًا"],
                    schedule: [
                        "50% عند الحجز",
                        "50% قبل المغادرة"
                    ]
                }
            }
        }
    };

    return (
        <div key={language} className="font-sans container mx-auto" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <Helmet>
                <title>{content[language].title}</title>
                <meta
                    name="description"
                    content={language === 'fr' 
                        ? "Découvrez notre programme Omra 2025"
                        : "اكتشف برنامج عمرة 2025"}
                />
            </Helmet>

            <section className="relative h-screen md:h-96 bg-cover bg-center text-center flex flex-col justify-center items-center pt-20"
                style={{ backgroundImage: `url(${heroBgImage})` }}>
                <div className="absolute inset-0 bg-black opacity-50"></div>
                <div className="relative z-10">
                    <h1 className="text-white text-4xl md:text-5xl font-bold mb-4 leading-tight">
                        {content[language].title}
                    </h1>
                    <p className="text-gray-100 text-lg md:text-xl mb-8">
                        {content[language].subtitle}
                    </p>
                    <a href="#programme" className="bg-green-500 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-md inline-block transition-colors duration-300 shadow-lg">
                        {content[language].discoverButton}
                    </a>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-16 px-4 bg-gray-50">
                <div className="container mx-auto">
                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
                        {content[language].servicesTitle}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
                        <ServiceItem 
                            image={hotelIcon} 
                            title={language === 'fr' ? "Hôtels" : "فنادق"} 
                            description={language === 'fr' 
                                ? "Hébergement de luxe proche des lieux saints" 
                                : "إقامة فاخرة قريبة من الأماكن المقدسة"} 
                        />
                        <ServiceItem 
                            image={transportIcon} 
                            title={language === 'fr' ? "Vol direct" : "رحلة مباشرة"} 
                            description={language === 'fr'                                 ? "Vols directs avec 2 valises de 23 kg" 
                                : "رحلات مباشرة مع أمتعة 2 × 23 كجم"} 
                        />
                        <ServiceItem 
                            image={visaIcon} 
                            title="Visa" 
                            description={language === 'fr' 
                                ? "Assistance complète pour le visa" 
                                : "مساعدة كاملة لإجراءات التأشيرة"} 
                        />
                        <ServiceItem 
                            image={guideIcon} 
                            title={language === 'fr' ? "Accompagnement" : "مرافقة"} 
                            description={language === 'fr' 
                                ? "Guides expérimentés" 
                                : "مرشدون ذوو خبرة"} 
                        />
                    </div>
                </div>
            </section>

            <section id="programme" className="py-16 px-4">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
                    {content[language].programmeTitle}
                    <span className="block w-24 h-1 bg-green-500 mx-auto mt-2 rounded-full"></span>
                </h2>
                
                <div className="container mx-auto">
                    <div className="flex justify-center mb-8 overflow-x-auto">
                        <div className="flex space-x-2 md:space-x-4">
                            {tabs[language].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tabKeys[language][tab])}
                                    className={`px-6 py-3 rounded-t-lg font-semibold text-sm md:text-base min-w-max ${
                                        activeTab === tabKeys[language][tab]
                                            ? `bg-green-500 text-white shadow-md`
                                            : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-xl shadow-lg">
                        {activeTab === "Medine" && (
                            <div className="w-full">
                                <h3 className="text-2xl font-semibold text-green-600 mb-4">
                                    {content[language].medineContent.title}
                                </h3>
                                <p className="text-gray-700 mb-6">
                                    {content[language].medineContent.description}
                                </p>
                                
                                <div className="mb-8">
                                    <h4 className="text-xl font-semibold mb-4">
                                        {language === 'fr' ? "Nos Hôtels à Médine" : "فنادقنا في المدينة المنورة"}
                                    </h4>
                                    {hotelGalleries.Medine.map((hotel, index) => (
                                        <div key={index} className="mb-12">
                                            <div className="bg-green-50 p-4 rounded-lg mb-4">
                                                <h4 className="flex items-center text-lg font-semibold text-green-700 mb-2">
                                                    <FaHotel className="mr-2" />
                                                    {hotel.name} <span className="ml-2 text-yellow-600">{hotel.category}</span>
                                                </h4>
                                                <p className="text-gray-700 mb-3">
                                                    <FaMapMarkerAlt className="inline mr-2 text-green-600" />
                                                    {hotel.distance}
                                                </p>
                                                <ul className="list-disc pl-5 space-y-1">
                                                    {hotel.features.map((feature, idx) => (
                                                        <li key={idx} className="text-gray-700 text-sm">{feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <Gallery 
                                                items={hotel.images} 
                                                title={language === 'fr' ? `Galerie ${hotel.name}` : `معرض صور ${hotel.name}`}
                                                language={language}
                                            />
                                        </div>
                                    ))}
                                </div>
                                
                                <h3 className="text-2xl font-semibold text-green-600 mb-4">
                                    {language === 'fr' ? "Lieux à Visiter" : "أماكن الزيارة"}
                                </h3>
                                {placesGalleries.Medine.map((place, index) => (
                                    <div key={index} className="mb-8">
                                        <div className="border-l-4 border-green-500 pl-4 mb-2">
                                            <h4 className="text-lg font-medium text-gray-800">{place.name}</h4>
                                            <p className="text-gray-600">{place.description}</p>
                                        </div>
                                        <Gallery 
                                            items={place.images} 
                                            title={language === 'fr' ? `Photos de ${place.name}` : `صور ${place.name}`}
                                            language={language}q
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                      
                        {activeTab === "Mecque" && (
                            <div className="w-full">
                                <h3 className="text-2xl font-semibold text-green-600 mb-4">
                                    {content[language].mekkaContent.title}
                                </h3>
                                <p className="text-gray-700 mb-6">
                                    {content[language].mekkaContent.description}
                                </p>
                                
                                <div className="mb-8">
                                    <h4 className="text-xl font-semibold mb-4">
                                        {language === 'fr' ? "Nos Hôtels à La Mecque" : "فنادقنا في مكة المكرمة"}
                                    </h4>
                                    {hotelGalleries.Mecque.map((hotel, index) => (
                                        <div key={index} className="mb-12">
                                            <div className="bg-green-50 p-4 rounded-lg mb-4">
                                                <h4 className="flex items-center text-lg font-semibold text-green-700 mb-2">
                                                    <FaHotel className="mr-2" />
                                                    {hotel.name} <span className="ml-2 text-yellow-600">{hotel.category}</span>
                                                </h4>
                                                <p className="text-gray-700 mb-3">
                                                    <FaMapMarkerAlt className="inline mr-2 text-green-600" />
                                                    {hotel.distance}
                                                </p>
                                                <ul className="list-disc pl-5 space-y-1">
                                                    {hotel.features.map((feature, idx) => (
                                                        <li key={idx} className="text-gray-700 text-sm">{feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <Gallery 
                                                items={hotel.images} 
                                                title={language === 'fr' ? `Galerie ${hotel.name}` : `معرض صور ${hotel.name}`}
                                                language={language}
                                            />
                                        </div>
                                    ))}
                                </div>
                                
                                <h3 className="text-2xl font-semibold text-green-600 mb-4">
                                    {language === 'fr' ? "Lieux à Visiter" : "أماكن الزيارة"}
                                </h3>
                                {placesGalleries.Mecque.map((place, index) => (
                                    <div key={index} className="mb-8">
                                        <div className="border-l-4 border-green-500 pl-4 mb-2">
                                            <h4 className="text-lg font-medium text-gray-800">{place.name}</h4>
                                            <p className="text-gray-600">{place.description}</p>
                                        </div>
                                        <Gallery 
                                            items={place.images} 
                                            title={language === 'fr' ? `Photos de ${place.name}` : `صور ${place.name}`}
                                            language={language}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === "Infos & Tarifs" && (
                            <div className="w-full">
                                <h3 className="text-2xl font-semibold text-green-600 mb-4">
                                    {content[language].infosTitle}
                                </h3>
                                
                                <div className="space-y-6">
                                    <div className="bg-green-50 p-4 rounded-lg">
                                        <h4 className="text-lg font-semibold text-green-700 mb-2">
                                            {language === 'fr' ? "✔️ Comprend :" : "✔️ تشمل:"}
                                        </h4>
                                        <ul className="list-disc pl-5 space-y-2">
                                            {content[language].infosContent.includes.map((item, index) => (
                                                <li key={index} className="text-gray-700">{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-amber-50 p-4 rounded-lg">
                                        <h4 className="text-lg font-semibold text-amber-700 mb-2">
                                            {language === 'fr' ? "✖️ Ne comprend pas :" : "✖️ لا تشمل:"}
                                        </h4>
                                        <ul className="list-disc pl-5 space-y-2">
                                            {content[language].infosContent.excludes.map((item, index) => (
                                                <li key={index} className="text-gray-700">{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <h4 className="text-lg font-semibold text-blue-700 mb-2">
                                            {language === 'fr' ? "📝 Documents requis :" : "📝 المستندات المطلوبة:"}
                                        </h4>
                                        <ul className="list-disc pl-5 space-y-2">
                                            {content[language].infosContent.documents.map((item, index) => (
                                                <li key={index} className="text-gray-700">{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-purple-50 p-4 rounded-lg">
                                        <h4 className="text-lg font-semibold text-purple-700 mb-2">
                                            {language === 'fr' ? "💰 Tarifs :" : "💰 الأسعار:"}
                                        </h4>
                                        <ul className="list-disc pl-5 space-y-2">
                                            {content[language].infosContent.prices.map((item, index) => (
                                                <li key={index} className="text-gray-700">{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                            {language === 'fr' ? "💳 Modalités de paiement :" : "💳 طرق الدفع:"}
                                        </h4>
                                        <p className="text-gray-700 mb-2">
                                            {language === 'fr' 
                                                ? "Modes de paiement acceptés : " 
                                                : "طرق الدفع المقبولة: "}
                                            {content[language].infosContent.payment.methods.join(", ")}
                                        </p>
                                        <p className="text-gray-700">
                                            {language === 'fr' 
                                                ? "Échéancier : " 
                                                : "جدول السداد: "}
                                            {content[language].infosContent.payment.schedule.join(" et ")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="text-center mt-8">
                        <a 
                            href={googleFormUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="mt-6 bg-green-500 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-md inline-block transition-colors duration-300 shadow-lg"
                        >
                            {content[language].reserveButton}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OmraAoutPage;